import { NetworkService } from "src/core/contract/NetworkService";
import { Event } from "src/core/model/Event";
import { EventResult } from "src/core/model/EventResult";
import { TrackerConfig } from "src/core/model/TrackerConfig";
import { TrackerConfiguration } from "src/core/model/TrackerConfiguration";
import { HttpClient } from "src/util/HttpClient";
import { Logger } from "src/util/Logger";

/**
 * Default network service implementation using Fetch API.
 * Mirrors Android DefaultNetworkService.
 */
export class DefaultNetworkService implements NetworkService {
  private configuration: TrackerConfiguration;

  constructor(configuration: TrackerConfiguration) {
    this.configuration = configuration;
  }

  async fetchConfiguration(): Promise<TrackerConfig> {
    const url = `${this.configuration.apiUrl}/sentinel/v1`;
    Logger.debug("Fetching configuration from:", url);

    const response = await HttpClient.get<TrackerConfig>(url, {
      headers: {
        "api-key": this.configuration.apiKey,
        tenant: this.configuration.tenant,
        platform: "web",
      },
      timeout: this.configuration.timeout,
    });

    Logger.debug("Received configuration:", JSON.stringify(response, null, 2));

    // Filter config: prefer WEB platform, fall back to first available
    const webConfig = response.configs.find((c) => c.platform === "WEB");
    const config: TrackerConfig = {
      ...response,
      configs: webConfig ? [webConfig] : response.configs,
    };

    return config;
  }

  async sendEvent(event: Event): Promise<EventResult | null> {
    // POST to same endpoint as config fetch (mirrors Android SDK behavior)
    const url = `${this.configuration.apiUrl}/sentinel/v1`;
    Logger.debug("Sending event:", event.name);

    // Strip internal-only fields before sending
    const { retryCount: _, ...payload } = event;
    return HttpClient.post<EventResult>(url, payload, {
      headers: {
        "api-key": this.configuration.apiKey,
        tenant: this.configuration.tenant,
        platform: "web",
      },
      timeout: this.configuration.timeout,
    });
  }

  isNetworkAvailable(): boolean {
    return navigator.onLine;
  }
}
